import React, { useState, useEffect, useContext, useRef } from 'react';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { DocumentTextIcon, EyeIcon, PencilIcon, DocumentArrowDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate } from '../lib/format';

interface Bill {
  id: string;
  user_id?: string;
  user_name?: string;
  bill_number: string;
  amount: number;
  balance: number;
  date: string;
  due_date: string;
  status: string;
  published: boolean;
  pdf_url?: string;
}

type ModalMode = 'create' | 'edit' | 'view' | null;

interface BillForm {
  user_id: string;
  bill_number: string;
  amount: string;
  balance: string;
  date: string;
  due_date: string;
  status: string;
  published: boolean;
  pdf: File | null;
}

const emptyForm: BillForm = { user_id: '', bill_number: '', amount: '', balance: '', date: '', due_date: '', status: 'unpaid', published: false, pdf: null };

export default function AdminBills() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [form, setForm] = useState<BillForm>(emptyForm);
  const [saving, setSaving] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [users, setUsers] = useState<{ id: string; username: string; email: string }[]>([]);

  useEffect(() => {
    fetchBills();
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.users || []);
      setUsers(list.map((u: any) => ({ id: u.id || u.sub, username: u.username || u.preferred_username || u.name || u.id, email: u.email || '' })));
    } catch {}
  };

  const fetchBills = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/admin/bills`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('Failed to fetch bills');
      const data = await res.json();
      setBills(data || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setForm(emptyForm); setFormError(null); setModalMode('create'); setSelectedBill(null); };
  const openEdit = (bill: Bill) => {
    setSelectedBill(bill);
    setForm({ user_id: bill.user_id || '', bill_number: bill.bill_number, amount: String(bill.amount), balance: String(bill.balance), date: bill.date?.split('T')[0] || '', due_date: bill.due_date?.split('T')[0] || '', status: bill.status, published: bill.published, pdf: null });
    setFormError(null);
    setModalMode('edit');
  };
  const openView = (bill: Bill) => { setSelectedBill(bill); setModalMode('view'); };
  const closeModal = () => { setModalMode(null); setSelectedBill(null); };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('user_id', form.user_id);
    fd.append('bill_number', form.bill_number);
    fd.append('amount', form.amount);
    fd.append('balance', form.balance);
    fd.append('date', form.date);
    fd.append('due_date', form.due_date);
    fd.append('status', form.status);
    fd.append('published', String(form.published));
    if (form.pdf) fd.append('pdf', form.pdf);
    return fd;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/bills`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}` },
        body: buildFormData(),
      });
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to create bill'); }
      closeModal();
      await fetchBills();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBill) return;
    setFormError(null);
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/bills/${selectedBill.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${keycloak?.token}` },
        body: buildFormData(),
      });
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to update bill'); }
      closeModal();
      await fetchBills();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bill: Bill) => {
    const ok = await confirm({ title: 'Delete bill', message: `Delete bill ${bill.bill_number}?`, confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/admin/bills/${bill.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to delete bill'); }
      await fetchBills();
    } catch (err: any) {
      toast(err.message, 'error');
    }
  };

  const handleDownload = async (bill: Bill) => {
    try {
      const res = await fetch(`${API_URL}/admin/bills/${bill.id}/pdf`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('PDF not available');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${bill.bill_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast(err.message, 'error');
    }
  };

  const formatPrice = (v: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);

  const FormFields = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-foreground">User</label>
        <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required
          className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary">
          <option value="">— Select a user —</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.username}{u.email ? ` (${u.email})` : ''}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground">Bill Number</label>
        <input type="text" value={form.bill_number} onChange={(e) => setForm({ ...form, bill_number: e.target.value })} required
          className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground">Amount</label>
        <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required
          className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground">Balance</label>
        <input type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} required
          className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground">Date</label>
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required
          className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground">Due Date</label>
        <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} required
          className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground">Status</label>
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
          className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary">
          <option value="unpaid">Unpaid</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input type="checkbox" id="published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}
          className="rounded border-border text-primary focus-visible:ring-ring" />
        <label htmlFor="published" className="text-sm font-medium text-foreground">Published</label>
      </div>
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-foreground">PDF File {modalMode === 'edit' && <span className="text-muted-foreground">(leave blank to keep existing)</span>}</label>
        <input ref={fileInputRef} type="file" accept="application/pdf" onChange={(e) => setForm({ ...form, pdf: e.target.files?.[0] || null })}
          className="mt-1 block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary/90" />
      </div>
    </div>
  );

  if (loading) return (
    <div className="space-y-6">
      <PageHeader
        icon={<DocumentTextIcon />}
        title="Bills"
        description="Create and manage customer bills."
      />
      <div className="rounded-lg border border-border bg-card p-6 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
  if (error) return <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">Error: {error}</p></div>;

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          icon={<DocumentTextIcon />}
          title="Bills"
          description="Create and manage customer bills."
          actions={
            <button type="button" onClick={openCreate}
              className="inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 shadow-sm cursor-pointer">
              New Bill
            </button>
          }
        />

        <div className="bg-card shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Bill</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Due</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {bills.length === 0 ? (
                  <tr><td colSpan={8} className="px-6 py-4 text-center text-sm text-muted-foreground">No bills found.</td></tr>
                ) : bills.map((bill) => (
                  <tr key={bill.id} className="hover:bg-muted/40">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{bill.bill_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{bill.user_name || bill.user_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{formatPrice(bill.amount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{formatPrice(bill.balance)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDate(bill.date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDate(bill.due_date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${bill.status === 'paid' ? 'bg-success/10 text-success ring-success/20' : bill.status === 'overdue' ? 'bg-destructive/10 text-destructive ring-destructive/20' : 'bg-warning/10 text-warning ring-warning/20'}`}>{bill.status.charAt(0).toUpperCase() + bill.status.slice(1)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => openView(bill)} title="View" className="p-1.5 rounded-md text-primary hover:bg-primary/10 cursor-pointer transition-colors"><EyeIcon className="h-4 w-4" /></button>
                        <button type="button" onClick={() => openEdit(bill)} title="Edit" className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 cursor-pointer transition-colors"><PencilIcon className="h-4 w-4" /></button>
                        <button type="button" onClick={() => handleDownload(bill)} title="Download PDF" className="p-1.5 rounded-md text-muted-foreground hover:bg-muted cursor-pointer transition-colors"><DocumentArrowDownIcon className="h-4 w-4" /></button>
                        <button type="button" onClick={() => handleDelete(bill)} title="Delete" className="p-1.5 rounded-md text-red-600 hover:bg-destructive/10 cursor-pointer transition-colors"><TrashIcon className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {(modalMode === 'create' || modalMode === 'edit') && (
          <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" onClick={closeModal} />
              <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                <form onSubmit={modalMode === 'create' ? handleCreate : handleEdit}>
                  <div className="bg-card px-4 pt-5 pb-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-foreground">{modalMode === 'create' ? 'New Bill' : `Edit ${selectedBill?.bill_number}`}</h3>
                      <button type="button" onClick={closeModal} className="text-muted-foreground hover:text-muted-foreground cursor-pointer">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    {formError && <div className="mb-4 rounded-md bg-destructive/10 p-3"><p className="text-sm text-red-700">{formError}</p></div>}
                    {FormFields}
                  </div>
                  <div className="bg-muted/30 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button type="submit" disabled={saving} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">{saving ? 'Saving…' : modalMode === 'create' ? 'Create Bill' : 'Save Changes'}</button>
                    <button type="button" onClick={closeModal} disabled={saving} className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/40 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {modalMode === 'view' && selectedBill && (
          <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" onClick={closeModal} />
              <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-card px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-4">{selectedBill.bill_number}</h3>
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div><dt className="font-medium text-foreground">User</dt><dd className="text-muted-foreground">{selectedBill.user_name || selectedBill.user_id}</dd></div>
                    <div><dt className="font-medium text-foreground">Amount</dt><dd className="text-muted-foreground">{formatPrice(selectedBill.amount)}</dd></div>
                    <div><dt className="font-medium text-foreground">Balance</dt><dd className="text-muted-foreground">{formatPrice(selectedBill.balance)}</dd></div>
                    <div><dt className="font-medium text-foreground">Date</dt><dd className="text-muted-foreground">{formatDate(selectedBill.date)}</dd></div>
                    <div><dt className="font-medium text-foreground">Due Date</dt><dd className="text-muted-foreground">{formatDate(selectedBill.due_date)}</dd></div>
                    <div><dt className="font-medium text-foreground">Status</dt><dd className="capitalize text-muted-foreground">{selectedBill.status}</dd></div>
                    <div><dt className="font-medium text-foreground">Published</dt><dd className="text-muted-foreground">{selectedBill.published ? 'Yes' : 'No'}</dd></div>
                  </dl>
                </div>
                <div className="bg-muted/30 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button type="button" onClick={() => handleDownload(selectedBill)} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 sm:w-auto sm:text-sm cursor-pointer">Download PDF</button>
                  <button type="button" onClick={closeModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/40 sm:mt-0 sm:w-auto sm:text-sm cursor-pointer">Close</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
